/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useEffect, useState } from 'react'
import { Form, Button, Header, Message, Grid, Segment, TabPane, GridColumn } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { usePrinter } from '../../../printing/services/printer.service'
import { AdminPrintingOptions } from '../../../printing/components/AdminPrintingOptions/AdminPrintingOptions'
import {
    registerWithPolling,
    registerWithPush,
    useDeviceInfo,
} from '../../../devices/device.provider'
import { DeviceOptions } from './DeviceOptions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getDevice, unregisterDevice } from '../../../services/Device/Device.service'
import useUser from '../../Shared/UserProvider/useUser'
import { ultyToast } from '../../Shared/UltyToast/UltyToast'
import { Troubleshooting } from './Troubleshooting'
import { AdminPricerOptions } from '../../../pricer/components/AdminPricerOptions/AdminPricerOptions'

const AdminDevice = () => {
    const [t] = useTranslation()
    const [loading, setLoading] = useState(false)
    const {deviceData, setDeviceData} = useDeviceInfo()
    const {printerInfo} = usePrinter()
    const queryClient = useQueryClient()
    const { user } = useUser()

    useEffect(() => {
        const handle = async (evt) => {
            setLoading(true)
            const token = evt.token
            await registerWithPush(token, true)
            ultyToast({ type: 'success', title: t('administration.device.link_success') })
            setLoading(false)
        }
        window.addEventListener('FirebaseToken', handle, false)
        return () => window.removeEventListener('FirebaseToken', handle)
    }, [t])

    const connectMutation = useMutation({
        mutationFn: async () => {
            if (window.webkit?.messageHandlers) {
                window.webkit.messageHandlers.getToken.postMessage('')
                return
            }

            const token = window.Android.getToken()
            return await registerWithPush(token, true)
        },
        onSuccess: (device) => {
            setDeviceData(device)
            ultyToast({ type: 'success', title: t('administration.device.link_success') })
        },
        onError: () => {
            ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
        },
    })

    const authConnectMutation = useMutation({
        mutationFn: registerWithPolling,
        onSuccess: (device) => {
            setDeviceData(device)
            ultyToast({ type: 'success', title: t('administration.device.link_success') })
        },
        onError: () => {
            ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
        },
    })

    const unregisterDeviceMutation = useMutation({
        mutationFn: () => {
            return unregisterDevice(deviceData.id)
        },
        onSuccess: () => {
            localStorage.removeItem('PUSH-TOKEN')
            localStorage.removeItem('TOKEN_SYNC')
            queryClient.removeQueries({
                queryKey: ['devices', deviceData.id],
            })
            setDeviceData(null)
            ultyToast({ type: 'success', title: t('administration.device.unlink_success') })
        },
        onError: () => {
            ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
        },
    })

    const {data: device} = useQuery({
        queryKey: ['devices', +deviceData?.id],
        queryFn: () => {
            return getDevice(deviceData.id)
        },
        enabled: !!deviceData?.id,
    });


    return (
      <TabPane>
          <Segment basic>
              <Form>
                  <Header>{t('administration.device.header')}</Header>
                  <Grid>
                      <GridColumn width={16}>
                          {device && (
                            <>
                                <Message color="teal">{t('administration.device.already_linked')}</Message>
                                {user.provider?.role.name === 'POWER_USER' &&
                                  <Button
                                    css={css`
                                    &.ui.button {
                                        margin-bottom: 1rem;
                                    }
                                  `}
                                    disabled={unregisterDeviceMutation.isPending}
                                    loading={unregisterDeviceMutation.isPending}
                                    size="tiny"
                                    onClick={unregisterDeviceMutation.mutate}>{t('administration.device.unlink_from_account')}
                                  </Button>
                                }
                            </>
                          )}
                          <Button disabled={connectMutation.isPending} loading={connectMutation.isPending} color="teal"
                                  size="tiny"
                                  onClick={connectMutation.mutate}>{t('administration.device.link_to_account')}</Button>
                      </GridColumn>

                      {window.Android?.storeAuthToken && window.Android?.acknowledgeOrder && (
                        <GridColumn width={16}>
                            <Button disabled={authConnectMutation.isPending} loading={authConnectMutation.isPending}
                                    color="teal" size="tiny"
                                    onClick={authConnectMutation.mutate}>{t('administration.device.link_to_auth_account')}</Button>
                        </GridColumn>
                      )}

                      {window.Android?.quit && (
                        <GridColumn width={16}>
                            <Button disabled={loading} loading={loading} color="teal" size="tiny" onClick={() => {
                                window.Android.quit()
                            }}>{t('administration.device.quit')}</Button>
                        </GridColumn>
                      )}
                      {window.Android?.resetToken && (
                        <GridColumn width={16}>
                            <Button disabled={loading} loading={loading} color="teal" size="tiny" onClick={() => {
                                window.Android.resetToken()
                            }}>{t('administration.device.reset')}</Button>
                        </GridColumn>
                      )}
                  </Grid>
              </Form>
          </Segment>

          {device && window.Android?.handleShortSound && (
            <DeviceOptions device={device} />
          )}

          {printerInfo.isDeviceCompatible && (<AdminPrintingOptions/>)}
          <AdminPricerOptions />

          {window.Android?.sendTroubleshootingInfo && user.isStaff && (<Troubleshooting/>)}

      </TabPane>
    )
}

export default AdminDevice
